import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Alert, Box, Chip, CircularProgress, Paper, TextField, Typography } from '@mui/material'
import { StyledButton } from '../../styles/StyledComponents'
import {
  addCompanyOwner,
  demoteCompanyOwner,
  fetchCompanyOwners,
  makeSoleCompanyOwner,
  type CompanyOwner,
} from '../../../../services/rbacService'
import { useRbacStore } from '../../../../stores/rbacStore'
import { addNotification } from '../../../../stores/NotificationStore'
import store from '../../../../stores'
import { describeAccessError } from '../../../../utils/rbacMessages'
import { OwnerChangeDialog, type OwnerChangeMode } from './OwnerChangeDialog'
import type { ManageAccessMember } from './ManageAccessDrawer'

interface CompanyOwnersSectionProps {
  companyId: string
  companyName: string
  /** Active members of the company, from whom a new Owner is picked. */
  members: readonly ManageAccessMember[]
  /** Called after any change, so the roster shows the new roles. */
  onChanged: () => void
}

interface Pending {
  mode: OwnerChangeMode
  target: { userId: string; name: string }
}

const ownerName = (owner: CompanyOwner) => owner.displayName || owner.email || owner.userId

/**
 * Platform admins only (SC-439): the one place Owners are added, demoted or replaced. The server
 * checks the caller on every call; the parent only mounts this for a verified platform admin.
 * A company may have several Owners but always keeps one active Owner, which the server enforces.
 */
export const CompanyOwnersSection: React.FC<CompanyOwnersSectionProps> = ({
  companyId,
  companyName,
  members,
  onChanged,
}) => {
  const [owners, setOwners] = useState<CompanyOwner[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [adding, setAdding] = useState(false)
  const [candidateId, setCandidateId] = useState('')
  const [pending, setPending] = useState<Pending | null>(null)
  // Without the list the other controls would act blind, so a failed load shows only a retry.
  const [loadFailed, setLoadFailed] = useState(false)
  const [reloads, setReloads] = useState(0)

  const fail = (failure: unknown) =>
    setError(describeAccessError(failure, useRbacStore.getState().catalogue))

  useEffect(() => {
    let cancelled = false
    setOwners(null)
    setError(null)
    setLoadFailed(false)
    fetchCompanyOwners(companyId).then(
      (list) => !cancelled && setOwners(list),
      (failure) => {
        if (cancelled) return
        setLoadFailed(true)
        fail(failure)
      }
    )
    return () => {
      cancelled = true
    }
  }, [companyId, reloads])

  const candidates = useMemo(() => {
    const ownerIds = new Set((owners ?? []).map((owner) => owner.userId))
    return members.filter((m) => !ownerIds.has(m.userId))
  }, [members, owners])

  // A changed Owner may be the platform admin's own account, so their own answer is stale too.
  const changed = useCallback(() => {
    void useRbacStore.getState().refresh()
    onChanged()
  }, [onChanged])

  const candidate = candidates.find((m) => m.userId === candidateId)

  const add = async () => {
    if (!candidate) return
    setAdding(true)
    setError(null)
    try {
      setOwners(await addCompanyOwner(companyId, candidate.userId))
      setCandidateId('')
      store.dispatch(
        addNotification({
          message: `${candidate.name} is now an Owner.`,
          type: 'success',
          duration: 5000,
        })
      )
      changed()
    } catch (failure) {
      fail(failure)
    } finally {
      setAdding(false)
    }
  }

  const confirm = async (roleKey: string) => {
    if (!pending) return
    const { mode, target } = pending
    if (mode === 'demote') {
      setOwners(await demoteCompanyOwner(companyId, target.userId, roleKey))
      store.dispatch(
        addNotification({
          message: `${target.name} is no longer an Owner.`,
          type: 'success',
          duration: 5000,
        })
      )
    } else {
      await makeSoleCompanyOwner(companyId, target.userId, roleKey)
      // From here the change is made: a failed re-read is a list problem, not a failed change, so
      // it must not reopen the dialog as if the admin should confirm again.
      store.dispatch(
        addNotification({
          message: `${target.name} is now the only Owner.`,
          type: 'success',
          duration: 5000,
        })
      )
      setError(null)
      changed()
      // The reply names only ids, so the list is read again for the names.
      try {
        setOwners(await fetchCompanyOwners(companyId))
      } catch (failure) {
        setLoadFailed(true)
        fail(failure)
      }
      return
    }
    setError(null)
    changed()
  }

  const targetId = pending?.target.userId
  const otherOwnerNames = (owners ?? []).filter((o) => o.userId !== targetId).map(ownerName)
  const many = (owners?.length ?? 0) > 1

  return (
    <Paper variant="outlined" sx={{ p: 2, mb: 2 }} data-testid="company-owners-section">
      <Typography variant="subtitle1" sx={{ color: 'text.primary', fontWeight: 600 }}>
        Owners
      </Typography>
      <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1.5 }}>
        A workspace can have several Owners and always keeps at least one active Owner. Only
        platform admins add, demote or replace Owners.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 1.5 }} data-testid="company-owners-error">
          {error}
        </Alert>
      )}

      {loadFailed ? (
        <StyledButton
          size="small"
          variant="outlined"
          onClick={() => setReloads((n) => n + 1)}
          data-testid="company-owners-retry"
        >
          Try again
        </StyledButton>
      ) : owners === null ? (
        <CircularProgress size={20} data-testid="company-owners-loading" />
      ) : (
        owners.map((owner) => (
          <Box
            key={owner.userId}
            data-testid={`company-owner-${owner.userId}`}
            sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', py: 0.5 }}
          >
            <Box sx={{ flex: 1, minWidth: 180 }}>
              <Typography variant="body2" sx={{ color: 'text.primary' }}>
                {ownerName(owner)}
              </Typography>
              {owner.email && owner.displayName && (
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  {owner.email}
                </Typography>
              )}
            </Box>
            <Chip
              size="small"
              label={owner.isActive ? 'Active' : 'Inactive'}
              color={owner.isActive ? 'success' : 'default'}
              variant="outlined"
            />
            <StyledButton
              size="small"
              variant="outlined"
              data-testid={`company-owner-demote-${owner.userId}`}
              onClick={() =>
                setPending({
                  mode: 'demote',
                  target: { userId: owner.userId, name: ownerName(owner) },
                })
              }
            >
              Demote
            </StyledButton>
            {many && owner.isActive && (
              <StyledButton
                size="small"
                variant="outlined"
                data-testid={`company-owner-sole-${owner.userId}`}
                onClick={() =>
                  setPending({
                    mode: 'sole',
                    target: { userId: owner.userId, name: ownerName(owner) },
                  })
                }
              >
                Make sole Owner
              </StyledButton>
            )}
          </Box>
        ))
      )}

      {owners !== null && !loadFailed && (
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap', mt: 1.5 }}>
          <TextField
            select
            size="small"
            value={candidateId}
            onChange={(event) => setCandidateId(event.target.value)}
            disabled={adding || candidates.length === 0}
            SelectProps={{ native: true }}
            inputProps={{
              'aria-label': 'Member to make Owner',
              'data-testid': 'company-owner-candidate',
            }}
            sx={{ minWidth: 240 }}
          >
            <option value="">
              {candidates.length === 0 ? 'No other active members' : 'Choose a member'}
            </option>
            {candidates.map((m) => (
              <option key={m.userId} value={m.userId}>
                {m.name}
                {m.email ? ` (${m.email})` : ''}
              </option>
            ))}
          </TextField>
          <StyledButton
            size="small"
            variant="contained"
            disabled={!candidate || adding}
            onClick={add}
            data-testid="company-owner-add"
          >
            {adding ? 'Adding...' : 'Add as Owner'}
          </StyledButton>
          <StyledButton
            size="small"
            variant="outlined"
            disabled={!candidate || adding}
            onClick={() =>
              candidate &&
              setPending({
                mode: 'sole',
                target: { userId: candidate.userId, name: candidate.name },
              })
            }
            data-testid="company-owner-replace"
          >
            Make sole Owner
          </StyledButton>
        </Box>
      )}

      <OwnerChangeDialog
        open={!!pending}
        mode={pending?.mode ?? 'demote'}
        companyId={companyId}
        companyName={companyName}
        target={pending?.target ?? null}
        otherOwnerNames={otherOwnerNames}
        onConfirm={confirm}
        onClose={() => setPending(null)}
      />
    </Paper>
  )
}

export default CompanyOwnersSection
